import { AbstractGoodgameClip } from "./AbstractGoodgameClip";
import { fatal } from "../../logging/fatal";
import { ClipLayerInfo } from "../analyse/ClipLayerInfo";
import { ClipColor } from "../components/ClipColor";
import { ClipSizeComponent } from "../components/ClipSizeComponent";
import { FlattenedImage } from "../clipsource/FlattenedImage";
import { ClipSourceOptimizer } from "../clipsource/ClipSourceOptimizer";
import { assetsPool } from "../../utils/AssetsPool";

import DisplayObject = createjs.DisplayObject;
import MovieClip = createjs.MovieClip;
import Sprite = createjs.Sprite;
import Event = createjs.Event;
import Rectangle = createjs.Rectangle;
import Container = createjs.Container;
import Bitmap = createjs.Bitmap;

export class AbstractDisplayObjectClip extends AbstractGoodgameClip {
    /** container to calculate registration point of displayobject. */
    private static containerRegPointFinder: Container = new Container();
    /** rectangle to find position of registration point. */
    private static regPointFinder: Rectangle = new Rectangle();

    public hasSubLayer: boolean;

    /** enable or disable mouse interaction for this component */
    public set isInteractive(value: boolean) {
        this.mouseChildren = value;
        this.mouseEnabled = value;
    }

    protected _clipColor: ClipColor[];

    public set colorData(value: ClipColor[]) {
        this._clipColor = value;
    }

    protected _animationColorChange: boolean;

    protected _clipLayerInfo: ClipLayerInfo;

    public get clipLayerInfo(): ClipLayerInfo {
        return this._clipLayerInfo;
    }

    protected _clipBounds: Rectangle = new Rectangle();

    public get clipBounds(): Rectangle {
        return this._clipBounds;
    }

    protected currentDisplayObject: DisplayObject;

    public recycleAsset: boolean = true;

    constructor() {
        super();
        this.addEventListener(Event.REMOVED_FROM_STAGE, this.bindFunction(this.handleRemovedFromStage));
    }

    protected initCurrentClip(sourceClass: any): void {
        if (sourceClass) {
            this.currentDisplayObject = assetsPool.obtain(sourceClass);

            let currentMC = this.currentDisplayObject;

            if (this._clipColor && this._clipColor.length > 0) {
                for (let cC of this._clipColor) {
                    cC.colorClip(currentMC);
                }
            }

            if (currentMC instanceof MovieClip && currentMC.framesLoaded >= 1) {
                this._endFrame = currentMC.framesLoaded;

                if (this.hasSubLayer) {
                    for (let j: number = 0; j < currentMC.numChildren - 1; j++) {
                        let subMC = currentMC.getChildAt(j);
                        if (subMC instanceof MovieClip) {
                            this._endFrame = Math.max(this._endFrame, subMC.framesLoaded);
                        }
                    }
                }
            } else {
                this._endFrame = 1;
            }

            if (this._running) {
                this.play();
            } else {
                this.stop();
            }

            this.refreshBounds(this.currentDisplayObject);

            this.jumpToFrame(1);
            this.addChild(this.currentDisplayObject);
        }
    }

    protected jumpToFrame(frame: number): void {
        if (frame > 0) {
            this._currentFrame = frame;
            let currentMC: MovieClip = (<MovieClip>this.currentDisplayObject);

            if (currentMC instanceof MovieClip) {
                currentMC.gotoAndStop(this._currentFrame);

                if (this.hasSubLayer) {
                    for (let i: number = 0; i < currentMC.numChildren; i++) {
                        let subMC = currentMC.getChildAt(i);
                        if (subMC instanceof MovieClip) {
                            subMC.gotoAndStop(this._currentFrame % subMC.totalFrames === 0 ? subMC.totalFrames : this._currentFrame % subMC.totalFrames);
                        }
                    }
                }

                if (this._animationColorChange && this._clipColor && this._clipColor.length > 0) {
                    for (let cC of this._clipColor) {
                        cC.colorClip(this.currentDisplayObject);
                    }
                }
            }
        } else {
            let errorString: string = "cannot jump to frame. invalid frame number: " + frame;
            fatal(errorString);
        }
    }

    private handleRemovedFromStage(event: Event): void {
        if (this._clipSizeComponent) {
            this._clipSizeComponent.clipSizeChanged.removeAll();
        }
        this.removeEventListener(Event.REMOVED_FROM_STAGE, this.bindFunction(this.handleRemovedFromStage));

        this.stop();
    }

    public set clipSizeComponent(clipSize: ClipSizeComponent) {
        this._clipSizeComponent = clipSize;

        if (this._clipSizeComponent) {
            this._clipSizeComponent.setImageSize(this, this._clipBounds);
            this._clipSizeComponent.clipSizeChanged.dispatch(this);
        }
    }

    public get clipSizeComponent(): ClipSizeComponent {
        return this._clipSizeComponent;
    }

    private addToClipBounds(bounds: Rectangle): void {
        this._clipBounds = this._clipBounds.union(bounds);
    }

    protected refreshBounds(currentDO: DisplayObject): void {
        this._clipBounds = new Rectangle();

        if (currentDO instanceof MovieClip) {
            // use first frame as bounds if children is empty
            // only used for EM, mainly for fixing placeholder position
            if (currentDO.children.length === 0) {
                currentDO.gotoAndStop(1);
            }
            this.addToClipBounds(currentDO.getBounds(null));
        } else if (currentDO instanceof Container) {
            this.addToClipBounds(currentDO.getBounds());
        }

        if (this._clipSizeComponent) {
            this._clipSizeComponent.setImageSize(this, this._clipBounds);
            this._clipSizeComponent.clipSizeChanged.dispatch(this);
        }
    }

    public get currentshownDisplayObject(): DisplayObject {
        return this.currentDisplayObject;
    }

    public get animationColorChange(): boolean {
        return this._animationColorChange;
    }

    public set animationColorChange(value: boolean) {
        this._animationColorChange = value;
    }

    public get clipColor(): ClipColor[] {
        return this._clipColor;
    }

    public set clipColor(value: ClipColor[]) {
        this._clipColor = value;
    }

    private renderSingleFrame(source: Container): FlattenedImage {
        AbstractDisplayObjectClip.containerRegPointFinder.addChild(source);
        AbstractDisplayObjectClip.regPointFinder = AbstractDisplayObjectClip.containerRegPointFinder.getBounds();
        AbstractDisplayObjectClip.containerRegPointFinder.removeChild(source);
        this.addToClipBounds(AbstractDisplayObjectClip.regPointFinder);

        let flatWidth: number = Math.ceil(source.width);
        let flatHeight: number = Math.ceil(source.height);

        // scaled children break the positions, we need to add an offset to fix those
        let scaledOffsetX = source.children.length > 0 && source.children[0].scaleX < 1 ? flatWidth / 2 : 0;
        let scaledOffsetY = source.children.length > 0 && source.children[0].scaleY < 1 ? flatHeight / 2 : 0;

        let flatRegX: number = -Math.floor(AbstractDisplayObjectClip.regPointFinder.x) + scaledOffsetX;
        flatWidth += Math.ceil(Math.abs(flatRegX + AbstractDisplayObjectClip.regPointFinder.x));
        let flatRegY: number = -Math.floor(AbstractDisplayObjectClip.regPointFinder.y) + scaledOffsetY;
        flatHeight += Math.ceil(Math.abs(flatRegY + AbstractDisplayObjectClip.regPointFinder.y));

        let flatImage = new FlattenedImage(flatWidth, flatHeight, flatRegX, flatRegY, undefined, true);

        if (source.children.length > 0) {
            source.cache(-flatRegX, -flatRegY, flatWidth, flatHeight);
        } else {
            source.cache(0, 0, source.width, source.height);
        }

        flatImage.draw(source);

        source.uncache();

        return flatImage;
    }

    protected convertMovieClip(source: MovieClip): Bitmap[] {
        let resultBitmaps = ClipSourceOptimizer.ClipMCToBitmaps(source, this, this.renderSingleFrame);
        return resultBitmaps;
    }

    public dispose(): void {
        if (this.currentDisplayObject && this.recycleAsset) {
            assetsPool.recycle(this.currentDisplayObject);
        }
        super.dispose();
    }
}
